import { AlertPosition } from "@components/alert/Alert.types";
import { WithAlert, withAlert } from "@components/alert/withAlert";
import { SendIcon } from "@components/icon";
import { formatDateToDateString, IDayInterval } from "@components/inputs/date/utils";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import { IEntity } from "@odata/BindingContext";
import { ActionTypeCode, FiscalYearStatusCode } from "@odata/GeneratedEnums";
import { logAction } from "@odata/OData.utils";
import { getMostCurrentFiscalYear, getSortedFYs } from "@pages/fiscalYear/FiscalYear.utils";
import { downloadFileFromUrl } from "@utils/FileStorage";
import { isNotDefined } from "@utils/general";
import React from "react";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

import IconSelect from "../../../components/inputs/select/IconSelect";
import { REST_API_URL } from "../../../constants";
import { QueryParam, Sort, Status, ToolbarItemType } from "../../../enums";
import { Model, ModelEvent } from "../../../model/Model";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { getUtcDayjs, TDateLike } from "../../../types/Date";
import { memoizeOne } from "../../../utils/memoizeOne";
import { TableActionOrder, TableButtonsAction, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";
import Page, { IProps as IPageProps } from "../../Page";
import {
    getReportDisplayType,
    getReportDisplayTypeToolbarButtons,
    handleReportDisplayTypeChange,
    resetReportDisplayType,
    setReportDisplayType
} from "../balanceSheet/BalanceSheet";
import { IBalanceSheetCustomData } from "../balanceSheet/BalanceSheet.utils";
import { CommonReportProps, FiscalYearComparison, ReportDisplayType } from "../CommonDefs";
import { commonReportTranslations, getReportDefinition, PrintReportType } from "../Report.utils";
import { ReportSplitPage } from "../ReportSplitPage";
import { ReportStorage } from "../ReportStorage";
import { IReportFilterChangeEvent, IReportViewBaseProps } from "../ReportView";
import {
    getDefinition,
    getShortOpenLines,
    IncomeStatementProps,
    IncomeStatementStructure,
    initialSortBy
} from "./IncomeStatementDef";
import SendIncomeStatementDialog from "./sendIncomeStatementDialog";

export enum IncomeStatementDelta {
    Yes = "Yes",
    No = "No",
    InPercent = "InPercent"
}

export const getIncomeStatementExportUrl = (storage: Model, isShortReport: boolean, options?: {
    printType?: PrintReportType;
    dateRange?: any;
    byDate?: TDateLike;
}): string => {
    const company = storage.context.getCompany().Id;
    const printType = options?.printType ?? storage.data.entity[IncomeStatementProps.incomeStatementStructure] === IncomeStatementStructure.ByNatureOfExpense ? PrintReportType.IncomeStatementByNature : PrintReportType.IncomeStatementByFunction;
    const dateRange = options?.dateRange ?? storage.data.entity[CommonReportProps.dateRange];
    const byDate = options?.byDate ?? storage.data.entity[CommonReportProps.byDate];
    return `${REST_API_URL}/IncomeStatementPdfExport/${printType}/${dateRange}/${formatDateToDateString(byDate)}/${isShortReport}?CompanyId=${company}`;
};

interface IProps extends IPageProps, WithAlert {
}

enum FiscalYearRange {
    ThisFiscalYear = "ThisFiscalYear",
    LastFiscalYear = "LastFiscalYear",
    FiscalYearBLast = "FiscalYearBLast"
}

class IncomeStatement extends Page<IProps> {
    static defaultProps = {
        getDef: getReportDefinition.bind(null, getDefinition)
    };

    storage: ReportStorage<IBalanceSheetCustomData>;

    onAfterLoad = async (storage: ReportStorage) => {
        this.storage = storage;

        setReportDisplayType(this.storage, getQueryParameters()?.[QueryParam.ReportDisplayType] as ReportDisplayType ?? getReportDisplayType(storage) ?? ReportDisplayType.Short);

        this.storage?.emitter.on(ModelEvent.VariantChanged, this.handleVariantChange);

        this.setByDateIfEmpty();
    };

    componentWillUnmount() {
        this.storage?.emitter.off(ModelEvent.VariantChanged, this.handleVariantChange);
    }

    handleVariantChange = (): void => {
        this.setByDateIfEmpty();
    };

    handleReportDisplayTypeChange = (key: string) => {
        handleReportDisplayTypeChange({
            key: key as ReportDisplayType,
            storage: this.storage,
            initialSortBy,
            openLines: getShortOpenLines(this.context)
        });

        this.forceUpdate();
    };

    handleOpenSendDialog = (args: ISelectionChangeArgs) => {
        this.storage.setCustomData({ sendingMessageDialogOpen: true, messageReportType: args.value as string });
        this.forceUpdate();
    };

    handleSendMessageSuccess = () => {
        this.props.setAlert({
            status: Status.Success,
            title: this.storage.t("Common:Validation.SuccessTitle"),
            subTitle: this.storage.t("DataBox:SendDialog.MessageHasBeenSent")
        });
    };

    get sendMessageToolbarMenu(): TTableToolbarItem {
        return {
            id: "sendMessage",
            itemType: ToolbarItemType.Custom,
            order: TableActionOrder.Fullscreen + 1,
            render: () => {
                const { rowCount, loaded } = this.storage.tableAPI?.getState() ?? {};
                return <IconSelect
                    key={"sendMessage"}
                    hotspotId={"sendMessage"}
                    title={this.props.t("DataBox:SendDialog.SendIncomeStatement")}
                    onChange={this.handleOpenSendDialog}
                    headerText={this.props.t("DataBox:SendDialog.Send")}
                    icon={<SendIcon/>}
                    isDisabled={!rowCount || !loaded}
                    items={[{
                        id: "short",
                        iconName: "ExportReportShort",
                        label: this.props.t("Components:Table.PdfExportReportShort")
                    }, {
                        id: "full",
                        iconName: "ExportReportFull",
                        label: this.props.t("Components:Table.PdfExportReportFull")
                    }]}/>;
            }
        };
    }

    renderCustomToolbarButtons = (): TTableToolbarItem[] => {
        return [
            getReportDisplayTypeToolbarButtons(this.handleReportDisplayTypeChange, getReportDisplayType(this.storage)),
            this.sendMessageToolbarMenu
        ];
    };

    handleExpand = () => {
        resetReportDisplayType(this.storage);
    };

    getFiscalYearByRange = (range: FiscalYearRange) => {
        const _getCorrectFy = (offset: number) => {
            const items = getSortedFYs(context, Sort.Asc);
            const currentFy = getMostCurrentFiscalYear(context);
            const index = items.findIndex(item => currentFy?.Id === item.Id);

            // pick first fy from current which is used
            for (let i = index; i >= 0; i--) {
                const fy = items[i];
                if (fy && fy.StatusCode !== FiscalYearStatusCode.NotUsed) {
                    return items[i - offset];
                }
            }

            return undefined;
        };

        const context = this.storage.context;
        switch (range) {
            case FiscalYearRange.ThisFiscalYear:
                return _getCorrectFy(0);

            case FiscalYearRange.LastFiscalYear:
                return _getCorrectFy(1);

            case FiscalYearRange.FiscalYearBLast:
                return _getCorrectFy(2);
        }
    }

    setByDateIfEmpty = () => {
        const byDate = this.storage.data.entity[CommonReportProps.byDate];
        if (isNotDefined(byDate)) {
            this.setupDateByRange();
        }
    }

    setupDateByRange = () => {
        const range = this.storage.data.entity[CommonReportProps.dateRange];

        if (range) {
            const fy = this.getFiscalYearByRange(range as FiscalYearRange);
            if (fy) {
                this.storage.setValueByPath(CommonReportProps.byDate, fy.DateEnd);
            }
        }
    }

    handleDateRangeChange = (args: IReportFilterChangeEvent): IEntity => {
        const bc = args.filterChange.bindingContext;

        if (bc.getNavigationPath() !== CommonReportProps.dateRange) {
            return args.settings;
        }

        const settings = { ...args.settings };
        const dateRange = args.filterChange.value as string;

        // set current date if it is inside the date range, otherwise use DateEnd
        // for others => set DateEnd
        const dateRangeInfo = this.storage.getInfo(bc);
        const dateRangeItem = dateRangeInfo.fieldSettings?.items?.find(item => item.id === dateRange);
        const today = getUtcDayjs();
        const dateRangeInterval: IDayInterval = dateRangeItem?.additionalData?.dateRange;
        const dateEnd = getUtcDayjs(dateRangeInterval?.to);
        const byDateDate: Date = (today.isSameOrBefore(dateEnd, "day") ? today : dateEnd).toDate();
        const byDate = formatDateToDateString(byDateDate);

        // call storage.handleFilterChange to properly store value in LocalSettings
        this.storage.handleFilterChange({
            bindingContext: bc.getParent().navigate(CommonReportProps.byDate),
            parsedValue: byDateDate,
            value: byDateDate
        });

        settings[CommonReportProps.byDate] = byDate;

        return settings;
    };

    handleFilterChange = (args: IReportFilterChangeEvent) => {
        const path = args.filterChange.bindingContext.getPath();

        if (path === CommonReportProps.dateRange) {
            args.settings = this.handleDateRangeChange(args);
        }

        if (path === CommonReportProps.comparison) {
            if (!args.settings[CommonReportProps.comparison] || args.settings[CommonReportProps.comparison] === FiscalYearComparison.None) {
                args.settings[CommonReportProps.showDelta] = IncomeStatementDelta.No;
            } else {
                args.settings[CommonReportProps.showDelta] = this.storage.data.entity[CommonReportProps.showDelta];
            }

            // call storage.handleFilterChange to properly store value in LocalSettings and storage
            this.storage.handleFilterChange({
                bindingContext: this.storage.data.bindingContext.navigate(CommonReportProps.showDelta),
                parsedValue: args.settings[CommonReportProps.showDelta],
                value: args.settings[CommonReportProps.showDelta]
            });
        }

        return args.settings;
    };

    handlePrintReportButtonClick = (buttonAction: TableButtonsAction) => {
        const isShortReport = buttonAction === TableButtonsAction.PdfExportReportShort;
        const url = getIncomeStatementExportUrl(this.storage, isShortReport);

        logAction({
            actionId: this.storage.id,
            actionType: ActionTypeCode.PDFExport,
            detail: JSON.stringify({
                CompanyId: this.storage.settings.CompanyId,
                IsShortReport: isShortReport,
                IncomeStatementStructure: this.storage.settings[IncomeStatementProps.incomeStatementStructure]
            })
        });

        downloadFileFromUrl(url);
    };

    getReportViewProps = memoizeOne((): Partial<IReportViewBaseProps> => {
        return {
            customToolbarButtons: this.renderCustomToolbarButtons,
            onFilterChange: this.handleFilterChange,
            onExpandAll: this.handleExpand,
            onCollapseAll: this.handleExpand,
            onPrintReportButtonClick: this.handlePrintReportButtonClick,
            showPrintReportButtons: true,
            expandable: true
        };
    });

    render = () => {
        if (!this.isReady()) {
            return null;
        }

        return (
            <>
                <ReportSplitPage
                    {...this.getMandatoryProps()}
                    reportViewProps={this.getReportViewProps()}
                    onTableStorageLoad={this.onAfterLoad}
                />
                {this.storage?.getCustomData().sendingMessageDialogOpen &&
                    <SendIncomeStatementDialog
                        onAfterSend={this.handleSendMessageSuccess}
                        storage={this.storage}/>}
                {this.props.alert}
            </>
        );
    };
}

export default withRouter(withTranslation([...commonReportTranslations, "DataBox"])(withAlert({
    autoHide: true,
    position: AlertPosition.CenteredBottom,
    style: { width: "400px" }
})(IncomeStatement)));